/**
 * Types for horizontal object positioning
 * @type 'left' | 'center' | 'right'
 */
export type OriginX = 'left' | 'center' | 'right';

/**
 * Types for vertical object positioning
 * @type 'top' | 'center' | 'bottom'
 */
export type OriginY = 'top' | 'center' | 'bottom';

/**
 * Base properties for all editor objects
 *
 * This interface defines the fundamental properties for positioning,
 * transforming, and rendering objects on the canvas.
 *
 * SSR-compatible - does not use browser-only APIs
 */
export interface BaseProps {
  /**
   * Left position relative to origin
   * @default 0
   */
  left: number;

  /**
   * Top position relative to origin
   * @default 0
   */
  top: number;

  /**
   * Object width
   * @default 0
   */
  width: number;

  /**
   * Object height
   * @default 0
   */
  height: number;

  /**
   * Horizontal transform origin of the object
   * @default 'left'
   */
  originX: OriginX;

  /**
   * Vertical transform origin of the object
   * @default 'top'
   */
  originY: OriginY;

  /**
   * Object rotation angle (in degrees)
   * @default 0
   */
  angle: number;

  /**
   * Horizontal flip
   * @default false
   */
  flipX: boolean;

  /**
   * Vertical flip
   * @default false
   */
  flipY: boolean;

  /**
   * Horizontal scale
   * @default 1
   */
  scaleX: number;

  /**
   * Vertical scale
   * @default 1
   */
  scaleY: number;

  /**
   * Skew along the X axis (in degrees)
   * @default 0
   */
  skewX: number;

  /**
   * Skew along the Y axis (in degrees)
   * @default 0
   */
  skewY: number;
}

/**
 * Default values for base properties
 */
export const basePropsDefaults: BaseProps = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  originX: 'left',
  originY: 'top',
  angle: 0,
  flipX: false,
  flipY: false,
  scaleX: 1,
  scaleY: 1,
  skewX: 0,
  skewY: 0,
};
